import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Search, LogOut, Feather, Heart, Rss } from "lucide-react";
import { Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../hooks/useFavorites";
import { API } from "../lib/api";

const navItems = [
  { to: "/", label: "Inicio", end: true },
  { to: "/prosa", label: "Prosa" },
  { to: "/versos", label: "Versos" },
  { to: "/audios", label: "Recitales" },
  { to: "/autor", label: "Sobre el autor" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative" style={{ background: "var(--rincon-bg)" }}>
      <Toaster richColors position="top-center" />
      <header
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{
          background: "rgba(253, 251, 247, 0.82)",
          borderColor: "rgba(232, 224, 213, 0.6)",
        }}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="logo-link"
          >
            <Feather
              className="w-6 h-6 transition-transform group-hover:-rotate-12"
              style={{ color: "var(--rincon-primary)" }}
            />
            <div className="leading-none">
              <div
                className="font-display text-2xl md:text-3xl tracking-tight"
                style={{
                  color: "var(--rincon-primary)",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                El rincón de <em className="italic">mis palabras</em>
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.32em] mt-1.5"
                style={{ color: "var(--rincon-secondary)" }}
              >
                Javilobo · autor
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-lg ink-link ${
                    isActive ? "font-semibold" : ""
                  }`
                }
                style={({ isActive }) => ({
                  color: isActive
                    ? "var(--rincon-primary)"
                    : "var(--rincon-text)",
                })}
              >
                {n.label}
              </NavLink>
            ))}
            <button
              onClick={() => navigate("/buscar")}
              className="p-2 rounded-full transition-colors hover:bg-[var(--rincon-surface)]"
              aria-label="Buscar"
              data-testid="nav-search-button"
            >
              <Search className="w-4 h-4" style={{ color: "var(--rincon-text)" }} />
            </button>
            <button
              onClick={() => navigate("/favoritos")}
              className="relative p-2 rounded-full transition-colors hover:bg-[var(--rincon-surface)]"
              aria-label="Guardados"
              data-testid="nav-favorites-button"
            >
              <Heart className="w-4 h-4" style={{ color: "var(--rincon-text)" }} />
              {favorites.length > 0 ? (
                <span
                  className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] rounded-full"
                  style={{ background: "var(--rincon-primary)", color: "var(--rincon-bg)" }}
                  data-testid="nav-favorites-badge"
                >
                  {favorites.length}
                </span>
              ) : null}
            </button>
            {user && user.role === "admin" ? (
              <div className="flex items-center gap-4">
                <NavLink
                  to="/admin"
                  data-testid="nav-admin"
                  className="font-display text-lg ink-link"
                  style={{ color: "var(--rincon-secondary)" }}
                >
                  Admin
                </NavLink>
                <button
                  onClick={logout}
                  className="p-2 rounded-full hover:bg-[var(--rincon-surface)]"
                  aria-label="Cerrar sesión"
                  data-testid="nav-logout-button"
                >
                  <LogOut className="w-4 h-4" style={{ color: "var(--rincon-text)" }} />
                </button>
              </div>
            ) : null}
          </nav>

          {/* Mobile: just search */}
          <button
            onClick={() => navigate("/buscar")}
            className="md:hidden p-2 rounded-full hover:bg-[var(--rincon-surface)]"
            aria-label="Buscar"
            data-testid="nav-search-button-mobile"
          >
            <Search className="w-4 h-4" style={{ color: "var(--rincon-text)" }} />
          </button>
        </div>
        {/* mobile nav */}
        <div className="md:hidden flex justify-center gap-8 pb-4">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-mobile-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `font-display text-base ink-link ${isActive ? "font-semibold" : ""}`
              }
              style={({ isActive }) => ({
                color: isActive ? "var(--rincon-primary)" : "var(--rincon-text)",
              })}
            >
              {n.label}
            </NavLink>
          ))}
          {user && user.role === "admin" ? (
            <NavLink
              to="/admin"
              data-testid="nav-mobile-admin"
              className="font-display text-base ink-link"
              style={{ color: "var(--rincon-secondary)" }}
            >
              Admin
            </NavLink>
          ) : null}
        </div>
      </header>

      <main className="relative z-10">
        <Outlet />
      </main>

      <footer
        className="mt-32 border-t"
        style={{ borderColor: "var(--rincon-border)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-14 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <div
              className="font-display text-3xl"
              style={{ color: "var(--rincon-text)" }}
            >
              El rincón de mis palabras
            </div>
            <p
              className="font-body italic mt-2 max-w-md"
              style={{ color: "var(--rincon-text-muted)" }}
            >
              «Escribir es el modo más hondo de leerse a uno mismo.»
            </p>
          </div>
          <div
            className="text-xs uppercase tracking-[0.25em] flex flex-col md:items-end gap-3"
            style={{ color: "var(--rincon-text-muted)" }}
          >
            <div className="flex items-center gap-5">
              <Link
                to="/favoritos"
                data-testid="footer-favorites-link"
                className="inline-flex items-center gap-2 ink-link"
                style={{ color: "var(--rincon-secondary)" }}
              >
                <Heart className="w-3.5 h-3.5" /> Guardados
              </Link>
              <a
                href={`${API}/feed.xml`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-rss-link"
                className="inline-flex items-center gap-2 ink-link"
                style={{ color: "var(--rincon-secondary)" }}
              >
                <Rss className="w-3.5 h-3.5" /> Suscríbete por RSS
              </a>
            </div>
            <div>
              © {new Date().getFullYear()} Javilobo — Todos los derechos reservados
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
